import React, { useState, useEffect, useCallback } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import FMBLogo from "../../../assests/FMBlogo.png"; // Import the image
import ReminderImage from "../../../assests/landscape/0.png"; // Import the reminder image
import { getActiveAds, getQueues } from "../../../api/api";
import {
    createPusherInstance,
    subscribeToQueueUpdates,
} from "../../../config/pusher";

const DisplayAd = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentQueueNumber, setCurrentQueueNumber] = useState("00");
    const [searchParams] = useSearchParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // Get branch_id from URL params
    const branchId = searchParams.get("branch_id");

    useEffect(() => {
        fetchActiveAds();
        fetchCurrentQueue();
    }, [branchId]);

    // Auto-refresh ads every 60 seconds
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            console.log("Auto-refreshing display ads...");
            fetchActiveAds();
        }, 60000); // 60 seconds

        return () => clearInterval(refreshInterval);
    }, [branchId]);

    // Fetch latest queue number for the branch
    const fetchCurrentQueue = useCallback(async () => {
        if (!branchId) {
            setCurrentQueueNumber("00");
            return;
        }

        try {
            const data = await getQueues(branchId);
            if (data && Array.isArray(data) && data.length > 0) {
                // Priority 1: Find the queue with status "SERVING" (currently being served)
                const servingQueue = data.find(
                    (q) => q.status === "SERVING" || q.status === "serving"
                );

                if (servingQueue) {
                    setCurrentQueueNumber(servingQueue.number || servingQueue.QueueNr || "00");
                    return;
                }

                // Priority 2: If no serving queue, get the first WAITING queue (next to be served)
                // Queues are ordered by QueueRegTime ascending, so first one is oldest/next
                const waitingQueue = data.find(
                    (q) => q.status === "WAITING" || q.status === "waiting"
                );

                if (waitingQueue) {
                    setCurrentQueueNumber(waitingQueue.number || waitingQueue.QueueNr || "00");
                    return;
                }

                // If no SERVING or WAITING queues found, show "00"
                setCurrentQueueNumber("00");
            } else {
                // No queues available
                setCurrentQueueNumber("00");
            }
        } catch (err) {
            console.error("Error fetching current queue:", err);
            setCurrentQueueNumber("00");
        }
    }, [branchId]);

    // Set up Pusher for real-time queue updates
    useEffect(() => {
        if (!branchId) return;

        const pusher = createPusherInstance();
        if (!pusher) return;

        const channel = subscribeToQueueUpdates(pusher, (data) => {
            // Handle different types of queue updates
            const queueData = data.queue || data.data?.queue || data;
            const action = data.action || data.data?.action;

            // Check if this update is for our branch
            const queueBranchId = queueData.QueueBranchID || queueData.branch_id;
            if (queueBranchId && queueBranchId.toString() !== branchId.toString()) {
                return; // Ignore updates from other branches
            }

            // Update queue number if the updated queue is being served
            if (
                queueData.QueueStatus === "SERVING" ||
                queueData.status === "SERVING" ||
                queueData.QueueStatus === "serving" ||
                queueData.status === "serving"
            ) {
                setCurrentQueueNumber(
                    queueData.QueueNr || queueData.number || "00"
                );
            } else if (
                queueData.QueueStatus === "ASSISTED" ||
                queueData.QueueStatus === "DONE" ||
                queueData.status === "ASSISTED" ||
                queueData.status === "DONE" ||
                queueData.status === "assisted" ||
                queueData.status === "done"
            ) {
                // If the current serving queue is done, fetch the latest queue
                fetchCurrentQueue();
            } else if (
                queueData.QueueStatus === "WAITING" ||
                queueData.status === "WAITING" ||
                queueData.QueueStatus === "waiting" ||
                queueData.status === "waiting"
            ) {
                // If a new queue is added or status changes to WAITING, fetch latest queue
                // This ensures we show the correct next queue number
                fetchCurrentQueue();
            }
        });

        return () => {
            if (channel) {
                channel.unbind("queue.updated");
            }
            if (pusher) {
                pusher.unsubscribe("queues");
                pusher.disconnect();
            }
        };
    }, [branchId, fetchCurrentQueue]);

    // Auto-refresh current queue every 5 seconds
    useEffect(() => {
        if (!branchId) return;

        const refreshInterval = setInterval(() => {
            fetchCurrentQueue();
        }, 5000); // 5 seconds

        return () => clearInterval(refreshInterval);
    }, [branchId, fetchCurrentQueue]);

    const fetchActiveAds = async () => {
        try {
            setLoading(true);
            const response = await getActiveAds("landscape", null, branchId);
            if (response.success) {
                setAds(response.data || []);
            } else {
                setError("Failed to load ads");
            }
        } catch (err) {
            setError("Error loading ads");
            console.error("Error fetching ads:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ads.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => {
                    // Calculate total items: ads + reminder images
                    // Every 4 ads, we insert 1 reminder image
                    const totalItems = ads.length + Math.floor(ads.length / 4);
                    return (prevIndex + 1) % totalItems;
                });
            }, 5000); // Change image every 5 seconds

            return () => clearInterval(interval);
        }
    }, [ads]);

    // Function to get the current image and alt text
    const getCurrentImage = () => {
        if (ads.length === 0) return { src: null, alt: "" };

        // Check if current index should show reminder image
        // Show reminder image after every 4 ads (at positions 4, 9, 14, etc.)
        if ((currentIndex + 1) % 5 === 0) {
            return {
                src: ReminderImage,
                alt: "Please collect your queue number",
            };
        }

        // Calculate which ad to show, accounting for inserted reminder images
        const adIndex = currentIndex - Math.floor(currentIndex / 5);
        return {
            src: ads[adIndex].image_url,
            alt: ads[adIndex].title,
        };
    };

    // Show loading or error state if needed
    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                height: "100vh",
                width: "100%"
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        height: isMobile ? "50vh" : "100vh",
                        width: isMobile ? "100%" : "25%",
                    }}
                >
                    <img
                        src={FMBLogo}
                        alt="FMB Logo"
                        style={{
                            width: "28%",
                            transition: "opacity 1s ease-in-out",
                            marginBottom: "50px",
                        }}
                    />
                    <div
                        style={{
                            fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                            marginTop: "-80px",
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                    </div>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: isMobile ? "100%" : "75%",
                        height: isMobile ? "50vh" : "100vh",
                        fontSize: isMobile ? "18px" : "24px",
                        color: "#666",
                    }}
                >
                    Loading ads...
                </Box>
            </Box>
        );
    }

    if (error || ads.length === 0) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                height: "100vh",
                width: "100%"
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        height: isMobile ? "50vh" : "100vh",
                        width: isMobile ? "100%" : "25%",
                    }}
                >
                    <img
                        src={FMBLogo}
                        alt="FMB Logo"
                        style={{
                            width: "28%",
                            transition: "opacity 1s ease-in-out",
                            marginBottom: "50px",
                        }}
                    />
                    <div
                        style={{
                            fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                            marginTop: "-80px",
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                    </div>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: isMobile ? "100%" : "75%",
                        height: isMobile ? "50vh" : "100vh",
                        fontSize: isMobile ? "18px" : "24px",
                        color: "#666",
                    }}
                >
                    {error || "No active landscape ads available"}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            height: "100vh",
            width: "100%"
        }}>
            {/* Left side - Queue number (25% width) */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    height: isMobile ? "50vh" : "100vh",
                    width: isMobile ? "100%" : "25%",
                }}
            >
                <img
                    src={FMBLogo}
                    alt="FMB Logo"
                    style={{
                        width: "28%",
                        transition: "opacity 1s ease-in-out",
                        marginBottom: "50px",
                    }}
                />
                <div
                    style={{
                        fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                        marginTop: "-80px",
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                </div>
            </Box>

            {/* Right side - Landscape ads (75% width) */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: isMobile ? "100%" : "75%",
                    height: isMobile ? "50vh" : "100vh",
                    padding: 1,
                    boxSizing: "border-box",
                }}
            >
                <img
                    src={getCurrentImage().src}
                    alt={getCurrentImage().alt}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        objectPosition: "center center",
                        transition: "opacity 1s ease-in-out",
                        display: "block",
                    }}
                    onLoad={(e) => {
                        // Ensure image scales properly on load
                        const img = e.target;
                        const container = img.parentElement;
                        if (container) {
                            const containerWidth = container.clientWidth;
                            const containerHeight = container.clientHeight;
                            const imgAspectRatio = img.naturalWidth / img.naturalHeight;
                            const containerAspectRatio = containerWidth / containerHeight;

                            // Adjust sizing based on aspect ratios
                            if (imgAspectRatio > containerAspectRatio) {
                                img.style.width = "100%";
                                img.style.height = "auto";
                            } else {
                                img.style.width = "auto";
                                img.style.height = "100%";
                            }
                        }
                    }}
                />
            </Box>
        </Box >
    );
};

export default DisplayAd;
